import { Button } from "@/components/ui/button";
import { LoadingIcon } from "@/components/ui/loading-icon";

/**
 * Render-only — the various pre-Zero loading / sign-in / error screens
 * that `AuthGate` toggles between. Each is its own tiny component so
 * the AuthGate body stays a flat ladder of conditional returns.
 */

export function AuthLoadingScreen() {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="h-[28px] shrink-0" data-tauri-drag-region />
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <LoadingIcon size={32} className="shrink-0 text-muted-foreground" />
        <span>Loading...</span>
      </div>
    </div>
  );
}

export function AuthTimeoutScreen({
  title,
  description,
  onSignOut,
}: {
  title: string;
  description: string;
  onSignOut: () => void;
}) {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div
        className="absolute top-0 left-0 right-0 h-[38px]"
        data-tauri-drag-region
      />
      <div className="w-96 space-y-4 text-center">
        <h1 className="text-xl font-semibold text-foreground">{title}</h1>
        <p className="text-sm text-muted-foreground">{description}</p>
        <div className="flex items-center justify-center gap-2">
          <Button onClick={() => window.location.reload()}>Retry</Button>
          <Button variant="outline" onClick={onSignOut}>
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AuthLoadingFullscreen() {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div
        className="absolute top-0 left-0 right-0 h-[38px]"
        data-tauri-drag-region
      />
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <LoadingIcon size={32} className="shrink-0 text-muted-foreground" />
        <span>Loading...</span>
      </div>
    </div>
  );
}

export function SignInScreen({
  authAction,
  authActionError,
  onSignIn,
  onSignUp,
}: {
  authAction: "signin" | "signup" | null;
  authActionError: string | null;
  onSignIn: () => void;
  onSignUp: () => void;
}) {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div
        className="absolute top-0 left-0 right-0 h-[38px]"
        data-tauri-drag-region
      />
      <div className="w-80 space-y-6 text-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Welcome to Ano</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Sign in to get started
          </p>
        </div>
        <div className="space-y-3">
          <Button
            onClick={onSignIn}
            disabled={authAction === "signin"}
            className="w-full"
          >
            {authAction === "signin" ? (
              <span className="inline-flex items-center gap-2">
                <LoadingIcon size={16} aria-hidden="true" />
                Redirecting...
              </span>
            ) : (
              "Sign in"
            )}
          </Button>
          <Button
            variant="secondary"
            onClick={onSignUp}
            disabled={authAction === "signup"}
            className="w-full"
          >
            {authAction === "signup" ? (
              <span className="inline-flex items-center gap-2">
                <LoadingIcon size={16} aria-hidden="true" />
                Redirecting...
              </span>
            ) : (
              "Create account"
            )}
          </Button>
        </div>
        {authActionError && (
          <p className="text-sm text-destructive" role="alert">
            {authActionError}
          </p>
        )}
      </div>
    </div>
  );
}

export function SessionRecoveryScreen({
  authAction,
  isAuthActionPending,
  onSignIn,
  onSignOut,
}: {
  authAction: "signin" | "signup" | null;
  isAuthActionPending: boolean;
  onSignIn: () => void;
  onSignOut: () => void;
}) {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div
        className="absolute top-0 left-0 right-0 h-[38px]"
        data-tauri-drag-region
      />
      <div className="w-96 space-y-4 text-center">
        <h1 className="text-xl font-semibold text-foreground">
          Session could not be restored
        </h1>
        <p className="text-sm text-muted-foreground">
          Ano could not fetch a valid access token. Please sign in again.
        </p>
        <div className="flex items-center justify-center gap-2">
          <Button onClick={onSignIn} disabled={isAuthActionPending}>
            {authAction === "signin" ? (
              <span className="inline-flex items-center gap-2">
                <LoadingIcon size={16} aria-hidden="true" />
                Redirecting...
              </span>
            ) : (
              "Sign in again"
            )}
          </Button>
          <Button variant="outline" onClick={onSignOut}>
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
